import {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import useMember from '@src/Store/selectors/useMember';
import packageJson from '../../../../package.json';
import {
  detectPlatform,
  setTealiumContext,
} from '../tealiumContext';
import {tealiumConfig} from '../tealiumConfig';

/**
 * Synchronise le contexte du tracker Tealium avec le store (member, locale)
 * et l'environnement (plateforme, version, parent domain).
 *
 * Monté une seule fois au démarrage (cf. InitTrackerUser). Toutes les
 * mises à jour ultérieures du membre / de la langue sont propagées au
 * data layer Tealium.
 */
const useTealiumContext = (): void => {
  const member = useMember();
  const {i18n} = useTranslation();

  // Init statique au montage
  useEffect(() => {
    setTealiumContext({
      siteVersion: packageJson.version || '0.0.0',
      parentDomain: tealiumConfig.parentDomain,
      country: tealiumConfig.defaultCountry,
      siteType: 'web',
      platform: detectPlatform(),
    });

    if (typeof window === 'undefined') return;
    const handler = () => setTealiumContext({platform: detectPlatform()});
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Sync membre
  useEffect(() => {
    if (member && (member as {_id?: string})._id) {
      setTealiumContext({
        visitorId: (member as {_id: string})._id,
        loginStatus: 'loggedin',
      });
    } else {
      setTealiumContext({
        visitorId: undefined,
        loginStatus: 'loggedout',
      });
    }
  }, [member]);

  // Sync locale i18next (mappée vers le format court attendu : en/fr/pt)
  useEffect(() => {
    const lang = (i18n.language || 'en').split('-')[0];
    setTealiumContext({locale: lang});
  }, [i18n.language]);
};

export default useTealiumContext;
